import React, { useState, useEffect } from 'react';
import {
    Store, Plus, AlertCircle, CheckCircle,
    Trash2, RefreshCw, ChevronDown, ChevronUp, Pause, Play,
    Video, ExternalLink, X
} from 'lucide-react';
import authService from '../services/AuthService';
import { MARKETPLACE_LOGOS, API_BASE } from '../consts';
import AIPVideosViewer from '../components/AIPVideosViewer';
import { StorefrontEmailSettings } from './StorefrontScanPage';

/* ──────────────────────────────────────────────
   Main Page
   ────────────────────────────────────────────── */
const AIPStorefrontScanPage = ({ user }) => {
    const [activeTab, setActiveTab] = useState('create');
    const [storefronts, setStorefronts] = useState([]);
    const [selectedStorefront, setSelectedStorefront] = useState(null);
    const [loadingStorefronts, setLoadingStorefronts] = useState(false);

    useEffect(() => {
        loadStorefronts();
    }, []);

    const loadStorefronts = async (silent = false) => {
        if (!silent) setLoadingStorefronts(true);
        try {
            const response = await authService.authenticatedFetch(API_BASE + '/api/aip/storefront/tracked');
            const data = await response.json();
            if (response.ok) {
                setStorefronts(data.storefronts || []);
            }
        } catch (error) {
            console.error('Failed to load AIP storefronts:', error);
        } finally {
            if (!silent) setLoadingStorefronts(false);
        }
    };

    const scrollToVideos = () => {
        const section = document.getElementById('aip-videos-section');
        if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
            {/* Animated background */}
            <div className="fixed inset-0 opacity-10 pointer-events-none">
                <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }} />
                <div className="absolute bottom-0 -right-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }} />
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                        <Store className="text-teal-600" size={40} />
                        AIP Storefront Stalking
                    </h1>
                    <p className="text-gray-600">
                        Track Amazon Influencer storefronts. Discover which products top influencers are promoting and find new AIP opportunities.
                    </p>
                </div>

                <StorefrontEmailSettings user={user} />

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-lg mb-6">
                    <div className="flex border-b">
                        <button
                            onClick={() => { setActiveTab('create'); setSelectedStorefront(null); }}
                            className={`flex-1 py-4 px-6 font-semibold transition ${activeTab === 'create'
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <Plus className="inline mr-2" size={20} />
                            Track New Storefront
                        </button>
                        <button
                            onClick={() => setActiveTab('storefronts')}
                            className={`flex-1 py-4 px-6 font-semibold transition flex items-center gap-1 justify-center ${activeTab === 'storefronts'
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <Store className="inline mr-2" size={20} />
                            Tracked Storefronts ({loadingStorefronts ? <RefreshCw className="animate-spin" size={16} /> : storefronts.length})
                        </button>
                    </div>

                    <div className="p-6">
                        {activeTab === 'create' ? (
                            <CreateStorefrontTab
                                onStorefrontCreated={() => { loadStorefronts(); setActiveTab('storefronts'); }}
                                existingStorefronts={storefronts}
                            />
                        ) : (
                            <MyStorefrontsTab
                                storefronts={storefronts}
                                loadingStorefronts={loadingStorefronts}
                                onStorefrontSelect={(storefront) => {
                                    setSelectedStorefront(storefront);
                                    setTimeout(() => scrollToVideos(), 100);
                                }}
                                onStorefrontDeleted={loadStorefronts}
                                onAutoUpdate={() => loadStorefronts(true)}
                            />
                        )}
                    </div>
                </div>

                {/* Selected Storefront Videos */}
                {selectedStorefront && (
                    <div id="aip-videos-section">
                        <AIPVideosViewer
                            storefront={selectedStorefront}
                            user={user}
                            onClose={() => setSelectedStorefront(null)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

/* ──────────────────────────────────────────────
   Create Storefront Tab
   ────────────────────────────────────────────── */
const CreateStorefrontTab = ({ onStorefrontCreated, existingStorefronts }) => {
    const [storefrontUrl, setStorefrontUrl] = useState('');
    const [loading, setLoading] = useState(false);

    const handleAddStorefront = async () => {
        if (!storefrontUrl.trim()) {
            alert('Please enter an Amazon Influencer storefront URL');
            return;
        }

        let url = storefrontUrl.trim();

        // Basic validation - must contain /shop/
        if (!url.includes('/shop/')) {
            alert('Invalid URL. Amazon Influencer storefront URLs must contain /shop/ (e.g., https://www.amazon.com/shop/influencername)');
            return;
        }

        // Add https:// if missing
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = 'https://' + url;
        }

        // Check duplicate
        if (existingStorefronts.some(s => s.storefront_url.toLowerCase() === url.toLowerCase())) {
            alert('This storefront is already being tracked.');
            return;
        }

        setLoading(true);
        try {
            const response = await authService.authenticatedFetch(API_BASE + '/api/aip/storefront/tracked', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ storefront_url: url })
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.error === 'MAX_SCANS_REACHED') {
                    alert(data.message);
                    return;
                }
                throw new Error(data.error || 'Failed to add storefront');
            }

            setStorefrontUrl('');
            onStorefrontCreated();
        } catch (error) {
            alert(error.message || 'Failed to add storefront');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* URL Input */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Influencer Storefront URL
                </label>
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={storefrontUrl}
                        onChange={(e) => setStorefrontUrl(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddStorefront()}
                        placeholder="https://www.amazon.com/shop/influencername"
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                    <button
                        onClick={handleAddStorefront}
                        disabled={loading}
                        className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition font-semibold flex items-center gap-2 disabled:opacity-50"
                    >
                        {loading ? <RefreshCw className="animate-spin" size={20} /> : <Plus size={20} />}
                        Track
                    </button>
                </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                    <AlertCircle className="inline mr-2" size={16} />
                    <strong>How it works:</strong><br />
                    Paste an Amazon Influencer's storefront URL (must contain <code className="bg-blue-100 px-1 py-0.5 rounded text-xs">/shop/</code>).
                    MSP will scan it daily, discover all their video reviews, and show you which ASINs they're promoting - helping you find AIP opportunities with proven demand.
                </p>
            </div>
        </div>
    );
};

/* ──────────────────────────────────────────────
   My Storefronts Tab
   ────────────────────────────────────────────── */
const MyStorefrontsTab = ({ storefronts, loadingStorefronts, onStorefrontSelect, onStorefrontDeleted, onAutoUpdate }) => {
    const [deletingId, setDeletingId] = useState(null);
    const [expandedStorefronts, setExpandedStorefronts] = useState(new Set());
    const [favorites, setFavorites] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem('aip_sf_favorites') || '[]')); }
        catch { return new Set(); }
    });
    const toggleFavorite = (id) => {
        setFavorites(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            localStorage.setItem('aip_sf_favorites', JSON.stringify([...next]));
            return next;
        });
    };
    const [notes, setNotes] = useState(() => {
        try { return JSON.parse(localStorage.getItem('aip_sf_notes') || '{}'); }
        catch { return {}; }
    });
    const saveNote = (id, text) => {
        setNotes(prev => {
            const next = { ...prev, [id]: text };
            localStorage.setItem('aip_sf_notes', JSON.stringify(next));
            return next;
        });
    };

    useEffect(() => {
        const interval = setInterval(() => onAutoUpdate(), 20000);
        return () => clearInterval(interval);
    }, []);

    const handleDelete = async (storefrontId) => {
        if (!window.confirm('Delete this storefront? All associated video data will be deleted.')) return;
        setDeletingId(storefrontId);
        try {
            await authService.authenticatedFetch(API_BASE + `/api/aip/storefront/tracked/${storefrontId}`, { method: 'DELETE' });
            onStorefrontDeleted();
        } catch {
            alert('Failed to delete storefront');
        } finally {
            setDeletingId(null);
        }
    };

    const handlePause = async (storefrontId) => {
        try {
            await authService.authenticatedFetch(API_BASE + `/api/aip/storefront/tracked/${storefrontId}/pause`, { method: 'POST' });
            onAutoUpdate();
        } catch {
            alert('Failed to pause storefront');
        }
    };

    const handleResume = async (storefrontId) => {
        try {
            const response = await authService.authenticatedFetch(API_BASE + `/api/aip/storefront/tracked/${storefrontId}/resume`, { method: 'POST' });
            const data = await response.json();
            if (!response.ok) {
                if (data.error === 'MAX_SCANS_REACHED') {
                    alert(data.message);
                    return;
                }
                throw new Error(data.error || 'Failed to resume');
            }
            onAutoUpdate();
        } catch (error) {
            alert(error.message || 'Failed to resume storefront');
        }
    };

    const toggleMinimize = (id) => {
        setExpandedStorefronts(prev => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };

    if (loadingStorefronts) {
        return (
            <div className="text-center py-12">
                <RefreshCw className="animate-spin mx-auto text-teal-600 mb-4" size={64} />
                <p className="text-gray-600 text-lg font-semibold">Loading storefronts…</p>
            </div>
        );
    }

    if (storefronts.length === 0) {
        return (
            <div className="text-center py-12">
                <Store className="mx-auto text-gray-400 mb-4" size={64} />
                <p className="text-gray-600 text-lg">No tracked AIP storefronts yet</p>
                <p className="text-gray-500 text-sm">Add your first influencer storefront to get started!</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {[...storefronts].sort((a, b) => {
                const aFav = favorites.has(a.id) ? 0 : 1;
                const bFav = favorites.has(b.id) ? 0 : 1;
                return aFav - bFav;
            }).map(storefront => (
                <AIPStorefrontCard
                    key={storefront.id}
                    storefront={storefront}
                    onSelect={() => onStorefrontSelect(storefront)}
                    onDelete={() => handleDelete(storefront.id)}
                    onPause={() => handlePause(storefront.id)}
                    onResume={() => handleResume(storefront.id)}
                    isDeleting={deletingId === storefront.id}
                    isMinimized={!expandedStorefronts.has(storefront.id)}
                    onToggleMinimize={() => toggleMinimize(storefront.id)}
                    isFavorite={favorites.has(storefront.id)}
                    onToggleFavorite={() => toggleFavorite(storefront.id)}
                    note={notes[storefront.id] || ''}
                    onSaveNote={(text) => saveNote(storefront.id, text)}
                />
            ))}

            <p className="text-xs text-gray-300 text-center" style={{ marginBottom: '-4px' }}>
                Running slow? Email contact@multisellerpro.com and we'll speed things up for you.
            </p>
        </div>
    );
};

/* ──────────────────────────────────────────────
   Storefront Card
   ────────────────────────────────────────────── */
const AIPStorefrontCard = ({ storefront, onSelect, onDelete, onPause, onResume, isDeleting, isMinimized, onToggleMinimize, isFavorite, onToggleFavorite, note, onSaveNote }) => {
    const videoStats = storefront.video_urls_count || { total: 0, pending: 0, processing: 0, completed: 0, failed: 0 };
    const [editingNote, setEditingNote] = useState(false);
    const [noteText, setNoteText] = useState(note);
    useEffect(() => { setNoteText(note); }, [note]);

    const getStatusBadge = () => {
        if (storefront.status === 'paused') {
            return (
                <span className="flex items-center gap-1 text-gray-700 bg-gray-100 px-3 py-1 rounded-full text-sm font-semibold">
                    <Pause size={16} /> Paused
                </span>
            );
        }
        return (
            <span className="flex items-center gap-1 text-green-700 bg-green-100 px-3 py-1 rounded-full text-sm font-semibold">
                <CheckCircle size={16} /> Scanning
            </span>
        );
    };

    // Extract influencer name from URL
    const getInfluencerName = () => {
        try {
            const match = storefront.storefront_url.match(/\/shop\/([^/?#]+)/i);
            return match ? match[1] : null;
        } catch {
            return null;
        }
    };

    const influencerName = storefront.store_name || getInfluencerName();

    // Minimized View
    if (isMinimized) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition">
                <div className="p-4 cursor-pointer" onClick={onToggleMinimize}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                            <button className="text-gray-600 hover:text-gray-800 p-1 hover:bg-gray-100 rounded">
                                <ChevronDown size={20} />
                            </button>

                            <button
                                onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                                className="p-1 hover:bg-yellow-50 rounded"
                                title={isFavorite ? 'Unpin' : 'Pin to top'}
                            >
                                <span className="text-xl">{isFavorite ? '⭐' : '☆'}</span>
                            </button>

                            <div className="flex items-center gap-3">
                                <img src={MARKETPLACE_LOGOS?.amazon} alt="Amazon" className="w-8 h-8 object-contain" />
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                        {influencerName || 'Scanning…'}
                                        {editingNote ? (
                                            <input
                                                autoFocus
                                                value={noteText}
                                                onChange={(e) => setNoteText(e.target.value)}
                                                onBlur={() => { onSaveNote(noteText); setEditingNote(false); }}
                                                onKeyDown={(e) => { if (e.key === 'Enter') { onSaveNote(noteText); setEditingNote(false); } if (e.key === 'Escape') setEditingNote(false); }}
                                                placeholder="Add a note..."
                                                className="text-sm font-normal border border-gray-300 rounded px-2 py-0.5 w-48 focus:ring-2 focus:ring-teal-400 focus:outline-none"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <span
                                                onClick={(e) => { e.stopPropagation(); setEditingNote(true); }}
                                                className="text-xs font-normal text-yellow-700 bg-yellow-50 border border-yellow-200 px-2 py-0.5 rounded-full max-w-xs truncate cursor-pointer hover:bg-yellow-100"
                                                title="Click to edit note"
                                            >
                                                {note ? note : '✏️ note...'}
                                            </span>
                                        )}
                                    </h3>
                                    <div className="text-xs text-gray-500 truncate max-w-md">
                                        {storefront.storefront_url}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {getStatusBadge()}

                            <div className="text-center">
                                <div className="text-xs text-gray-600">Reviews</div>
                                <div className="text-lg font-bold text-purple-800">{videoStats.total}</div>
                            </div>

                            <div className="text-center">
                                <div className="text-xs text-gray-600">Completed</div>
                                <div className="text-lg font-bold text-green-800">{videoStats.completed}</div>
                            </div>

                            <button
                                onClick={(e) => { e.stopPropagation(); onSelect(); }}
                                className="bg-gradient-to-r from-teal-500 to-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg transition"
                            >
                                View Videos
                            </button>

                            {storefront.status === 'active' ? (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onPause(); }}
                                    className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded"
                                    title="Pause"
                                >
                                    <Pause size={20} />
                                </button>
                            ) : (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onResume(); }}
                                    className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded"
                                    title="Resume"
                                >
                                    <Play size={20} />
                                </button>
                            )}

                            <button
                                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                                disabled={isDeleting}
                                className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded disabled:opacity-50"
                            >
                                {isDeleting ? <RefreshCw className="animate-spin" size={20} /> : <Trash2 size={20} />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Expanded View
    return (
        <div className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition p-6">
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2 flex-1">
                    <button onClick={onToggleMinimize} className="text-gray-600 hover:text-gray-800 p-1 hover:bg-gray-100 rounded">
                        <ChevronUp size={20} />
                    </button>

                    <button
                        onClick={onToggleFavorite}
                        className="p-1 hover:bg-yellow-50 rounded"
                        title={isFavorite ? 'Unpin' : 'Pin to top'}
                    >
                        <span className="text-xl">{isFavorite ? '⭐' : '☆'}</span>
                    </button>

                    <div className="flex items-center gap-3">
                        <img src={MARKETPLACE_LOGOS?.amazon} alt="Amazon" className="w-10 h-10 object-contain" />
                        <div>
                            <h3 className="text-xl font-bold text-gray-800">
                                {influencerName || 'Scanning…'}
                            </h3>
                            {editingNote ? (
                                <input
                                    autoFocus
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    onBlur={() => { onSaveNote(noteText); setEditingNote(false); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { onSaveNote(noteText); setEditingNote(false); } if (e.key === 'Escape') setEditingNote(false); }}
                                    placeholder="Add a note..."
                                    className="text-sm border border-gray-300 rounded px-2 py-1 w-64 focus:ring-2 focus:ring-teal-400 focus:outline-none"
                                />
                            ) : (
                                <div
                                    onClick={() => setEditingNote(true)}
                                    className="text-sm text-gray-400 hover:text-gray-700 cursor-pointer flex items-center gap-1"
                                    title="Click to add note"
                                >
                                    ✏️ {note ? <span className="text-yellow-700">{note}</span> : 'Add note...'}
                                </div>
                            )}
                            <div className="text-sm text-gray-500">
                                Last scanned: {storefront.last_scanned_at ? new Date(storefront.last_scanned_at).toLocaleString() : 'Never'}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {getStatusBadge()}

                    {storefront.status === 'active' ? (
                        <button onClick={onPause} className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded" title="Pause">
                            <Pause size={20} />
                        </button>
                    ) : (
                        <button onClick={onResume} className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded" title="Resume">
                            <Play size={20} />
                        </button>
                    )}

                    <button onClick={onDelete} disabled={isDeleting} className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded disabled:opacity-50">
                        {isDeleting ? <RefreshCw className="animate-spin" size={20} /> : <Trash2 size={20} />}
                    </button>
                </div>
            </div>

            {/* URL */}
            <div className="text-xs text-gray-500 mb-4 flex items-center gap-2">
                <span className="truncate">{storefront.storefront_url}</span>
                <a
                    href={storefront.storefront_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-800 flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                >
                    <ExternalLink size={14} />
                </a>
            </div>

            {/* Status Panel */}
            <div className="mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="grid grid-cols-5 gap-2">
                    <div className="text-center p-2 bg-purple-50 rounded border border-purple-200">
                        <div className="text-xs text-purple-600 font-semibold">Total Reviews</div>
                        <div className="text-xl font-bold text-purple-800">{videoStats.total}</div>
                    </div>
                    <div className="text-center p-2 bg-yellow-50 rounded">
                        <div className="text-xs text-yellow-600 font-semibold">Pending</div>
                        <div className="text-lg font-bold text-yellow-800">{videoStats.pending}</div>
                    </div>
                    <div className="text-center p-2 bg-blue-50 rounded">
                        <div className="text-xs text-blue-600 font-semibold">Processing</div>
                        <div className="text-lg font-bold text-blue-800">{videoStats.processing}</div>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded">
                        <div className="text-xs text-green-600 font-semibold">Completed</div>
                        <div className="text-lg font-bold text-green-800">{videoStats.completed}</div>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded">
                        <div className="text-xs text-red-600 font-semibold">Failed</div>
                        <div className="text-lg font-bold text-red-800">{videoStats.failed}</div>
                    </div>
                </div>
            </div>

            <button
                onClick={onSelect}
                className="w-full bg-gradient-to-r from-teal-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center justify-center gap-2"
            >
                <Video size={20} />
                View Videos
            </button>
        </div>
    );
};

export default AIPStorefrontScanPage;